'use client';

import React, { useState } from 'react';
import SchedulerPostPanel from './SchedulerPostPanel';
import SchedulerCalendar from './SchedulerCalendar';
import SchedulerHeader from './SchedulerHeader';

export interface SavedPost {
  id: string;
  platform: string;
  format: string;
  title: string;
  status: string;
  tone: string;
}

export interface ScheduledEntry {
  id: string;
  postId: string;
  post: SavedPost;
  date: string; // YYYY-MM-DD
  time: string; // HH:MM
  platform: string;
}

const savedPosts: SavedPost[] = [
  { id: 'sp-001', platform: 'LI', format: 'Experten-Artikel', title: 'Warum der Mittelstand KI falsch versteht', status: 'Freigegeben', tone: 'Business-Punk' },
  { id: 'sp-002', platform: 'IG', format: 'Reel-Script', title: 'Hinter den Kulissen: Unser Alltag', status: 'KI-generiert', tone: 'Authentisch' },
  { id: 'sp-003', platform: 'X', format: 'Thread', title: '5 Fehler bei Content-Planung', status: 'Freigegeben', tone: 'Provokativ' },
  { id: 'sp-004', platform: 'FB', format: 'Satire', title: 'Das Meeting-Bingo des deutschen Büros', status: 'Freigegeben', tone: 'Provokativ' },
  { id: 'sp-005', platform: 'LI', format: 'Storytelling', title: 'Der Tag, an dem wir fast alles verloren hätten', status: 'KI-generiert', tone: 'Human-Centric' },
  { id: 'sp-006', platform: 'IG', format: 'Caption', title: 'Produktneuheit Q2: Was sich verändert', status: 'Entwurf', tone: 'Authentisch' },
  { id: 'sp-007', platform: 'LI', format: 'Case Study', title: 'Case Study: Müller & Söhne — 40% Effizienz', status: 'Freigegeben', tone: 'Business-Punk' },
  { id: 'sp-008', platform: 'X', format: 'Meinung', title: 'Remote-Work 2026: Die unbequeme Wahrheit', status: 'Freigegeben', tone: 'Provokativ' },
];

export default function SchedulerLayout() {
  const [scheduledEntries, setScheduledEntries] = useState<ScheduledEntry[]>([
    { id: 'se-001', postId: 'sp-001', post: savedPosts[0], date: '2026-04-14', time: '09:00', platform: 'LI' },
    { id: 'se-002', postId: 'sp-003', post: savedPosts[2], date: '2026-04-15', time: '11:30', platform: 'X' },
    { id: 'se-003', postId: 'sp-004', post: savedPosts[3], date: '2026-04-16', time: '14:00', platform: 'FB' },
    { id: 'se-004', postId: 'sp-002', post: savedPosts[1], date: '2026-04-17', time: '10:00', platform: 'IG' },
  ]);
  const [currentMonth, setCurrentMonth] = useState<Date>(new Date(2026, 3, 1)); // April 2026
  const [draggingPost, setDraggingPost] = useState<SavedPost | null>(null);
  const [selectedEntry, setSelectedEntry] = useState<ScheduledEntry | null>(null);

  const handleSchedule = (post: SavedPost, date: string, time: string) => {
    const newEntry: ScheduledEntry = {
      id: `se-${Date.now()}`,
      postId: post.id,
      post,
      date,
      time,
      platform: post.platform,
    };
    setScheduledEntries((prev) => [...prev, newEntry]);
  };

  const handleRemoveEntry = (entryId: string) => {
    setScheduledEntries((prev) => prev.filter((e) => e.id !== entryId));
    if (selectedEntry?.id === entryId) setSelectedEntry(null);
  };

  const handleUpdateTime = (entryId: string, time: string) => {
    setScheduledEntries((prev) =>
      prev.map((e) => (e.id === entryId ? { ...e, time } : e))
    );
  };

  return (
    <section className="flex flex-col h-screen bg-background overflow-hidden" aria-label="Content Scheduler">
      <SchedulerHeader
        totalScheduled={scheduledEntries.length}
        currentMonth={currentMonth}
        onMonthChange={setCurrentMonth}
      />
      <div className="flex flex-1 overflow-hidden">
        <SchedulerPostPanel
          posts={savedPosts}
          scheduledPostIds={scheduledEntries.map((e) => e.postId)}
          draggingPost={draggingPost}
          onDragStart={setDraggingPost}
          onDragEnd={() => setDraggingPost(null)}
        />
        <SchedulerCalendar
          currentMonth={currentMonth}
          entries={scheduledEntries}
          draggingPost={draggingPost}
          selectedEntry={selectedEntry}
          onSelectEntry={setSelectedEntry}
          onSchedule={handleSchedule}
          onRemoveEntry={handleRemoveEntry}
          onUpdateTime={handleUpdateTime}
          onDragEnd={() => setDraggingPost(null)}
        />
      </div>
    </section>
  );
}
